import { FunctionComponent, ReactNode, useEffect } from 'react'
import PdMetaBanner from '../components/pd-meta-banner/pd-meta-banner'
import PdMetaBackgroundImageText from '../components/pd-meta-bgi-text/pd-meta-bgi-text'
import PdMetaComparisonTable from '../components/pd-meta-comparison-table/pd-meta-comparison-table'
import IconTagArray from '../components/standard/functionalModules/icon-tag-array'
import PdMetaImageTextCardArray from '../components/pd-meta-image-text-card/pd-meta-image-text-card'
import PdMetaProductSpecification from '../components/pd-meta-product-specification/pd-meta-product-specification'
import PdMetaScrollSwiperCard from '../components/pd-meta-scroll-swiper-card/pd-meta-scroll-swiper-card'
import TabSwiper from '../components/standard/functionalModules/tab-swiper/tab-swiper'
import PdMetaTextCard from '../components/pd-meta-text-card/pd-meta-text-card'
import NotesBar from '@components/standard/commonModules/notesBar'
import FullScreenTiling from '@components/standard/commonModules/full-screen-tiling'
import ProductParamsPanel from '@components/standard/commonModules/product-params-panel'
import AccessoriesPanel from '@components/standard/commonModules/accessories-panel'
import ComparePanel from '@components/standard/commonModules/compare-panel'
import FloatRobot from '@components/standard/commonModules/float-robot'
import MultiCardTiling from '@components/standard/commonModules/multi-card-tiling'
import FlipMultipleCards from '@components/standard/commonModules/flip-multiple-cards'
import FullScreenSingleMedia from '@/components/standard/functionalModules/full-screen-single-media'
import HorizontalSingleMedia from '@/components/standard/functionalModules/horizontal-single-media'
import VerticalSingleMedia from '@/components/standard/functionalModules/vertical-single-media'
import HorizontalMutipleMedia from '@/components/standard/functionalModules/horizontal-mutiple-media'
import { AspectRatio, IMetaComponent, SectionPaddingValue, TemplateType, Theme } from './pd-meta-components-types'
import MainAndTwoSubMedia from '@/components/standard/functionalModules/main-and-two-submedia'
import FeaturedMediaGrid from '@/components/standard/functionalModules/featured-media-grid'
import MainAndFourSubMedia from '@/components/standard/functionalModules/main-and-four-submedia'
import TopTabSwitch from '@/components/standard/functionalModules/top-tab-switch'
import FirstScreen from '@/components/standard/functionalModules/first-screen'
import ScrollSwitchMedia from '@/components/standard/speciallizedModules/scroll-switch-media'
import S1 from '@/components/standard/speciallizedModules/S1'
import S2 from '@/components/standard/speciallizedModules/S2'
import S4 from '@/components/standard/speciallizedModules/S4'
import SingleMediaAndTags from '@/components/standard/functionalModules/single-media-and-tags'
import useSWR from 'swr'
import { useLocale } from 'next-intl'
import { getProductDetailData, ProductDescriptionComponent, ProductDetailData } from '@/service/product'
import MultiCardModal from '@/components/standard/commonModules/multi-card-modal'
import { resolveMediaAlt } from './media-alt'

const mediaAspectRatioMap = {
  'ratio:1:1': AspectRatio.OneToOne,
  'ratio:1:2': AspectRatio.OneToTwo,
  'ratio:2:1': AspectRatio.TwoToOne,
  'ratio:2:3': AspectRatio.TwoToThree,
  'ratio:3:2': AspectRatio.ThreeToTwo,
  'ratio:3:4': AspectRatio.ThreeToFour,
  'ratio:4:3': AspectRatio.FourToThree,
  'ratio:9:16': AspectRatio.NineToSixteen,
  'ratio:16:9': AspectRatio.SixteenToNine,
}

function resolveMetaComponent(
  mp: ProductDescriptionComponent,
  productCode: string,
  locale: string
): {
  component: FunctionComponent<any>
  props: IMetaComponent['data']
} {
  const template = mp.__component
  switch (template) {
    case TemplateType.F0: // F0
      return {
        component: FirstScreen,
        props: {
          theme: mp?.theme,
          mediaUrl: mp?.mediaUrl?.url,
          mediaH5Url: mp?.mediaH5Url?.url,
          mediaAlt: resolveMediaAlt(mp?.mediaUrl?.alts, locale),
          mediaH5Alt: resolveMediaAlt(mp?.mediaH5Url?.alts, locale),
          titleUrl: mp?.titleUrl?.url,
          subTitle: mp?.subTitle,
          subTitleTag: mp?.subTitleTag,
          videoUrl: mp?.videoUrl?.url,
          maskColor: mp?.maskColor,
          maskDegree: mp?.maskDegree,
        },
      }
    case TemplateType.F11: // F1-1
      return {
        component: FullScreenSingleMedia,
        props: {
          title: mp?.title,
          titleTag: mp?.titleTag,
          desc: mp?.desc,
          mediaUrl: mp?.mediaUrl?.url,
          mediaH5Url: mp?.mediaH5Url?.url,
          theme: mp?.theme,
          maskColor: mp?.maskColor,
          maskDegree: mp?.maskDegree,
        },
      }
    case TemplateType.F12: // F1-2
      return {
        component: HorizontalSingleMedia,
        props: {
          theme: mp?.theme,
          mediaGroup: mp?.mediaGroup?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
              infoGroup: item?.infoGroup?.map((item: any) => {
                return {
                  ...item,
                  icon: item?.icon?.url,
                }
              }),
            }
          }),
          title: mp?.title,
          titleTag: mp?.titleTag,
          itemTitleTag: mp?.itemTitleTag,
          desc: mp?.desc,
          mediaAspectRatio: mediaAspectRatioMap[mp?.mediaAspectRatio as keyof typeof mediaAspectRatioMap],
          mediaBorderRadius: mp?.mediaBorderRadius,
          paddingTop: mp?.paddingTop  as SectionPaddingValue  || "px120",
          paddingBottom: mp?.paddingBottom  as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F13: // F1-3
      return {
        component: VerticalSingleMedia,
        props: {
          theme: mp?.theme,
          title: mp?.title,
          titleTag: mp?.titleTag,
          desc: mp?.desc,
          tagGroup: mp?.tagGroup?.map((item: any) => {
            return {
              ...item,
              icon: item?.icon?.url,
            }
          }),
          mediaGroup: mp?.mediaGroup?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
              mediaH5Url: item?.mediaH5Url?.url,
            }
          }),
          mediaAspectRatio: mediaAspectRatioMap[mp?.mediaAspectRatio as keyof typeof mediaAspectRatioMap],
          mediaAspectRatioH5: mp?.mediaAspectRatioH5
            ? mediaAspectRatioMap[mp?.mediaAspectRatioH5 as keyof typeof mediaAspectRatioMap]
            : undefined,
          mediaBorderRadius: mp?.mediaBorderRadius,
          paddingTop: mp?.paddingTop as SectionPaddingValue  || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F14: // F1-4
      return {
        component: SingleMediaAndTags,
        props: {
          type: 'F1-4',
          theme: mp?.theme,
          title: mp?.title,
          desc: mp?.desc,
          mediaUrl: mp?.mediaUrl?.url,
          mediaH5Url: mp?.mediaH5Url?.url,
          tagGroup: mp?.tagGroup?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
            }
          }),
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F15: // F1-5
      return {
        component: SingleMediaAndTags,
        props: {
          type: 'F1-5',
          theme: mp?.theme,
          title: mp?.title,
          desc: mp?.desc,
          mediaUrl: mp?.mediaUrl?.url,
          mediaH5Url: mp?.mediaH5Url?.url,
          tagGroup: mp?.tagGroup?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
            }
          }),
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F21: // F2-1
      return {
        component: HorizontalMutipleMedia,
        props: {
          type: 'F2-1',
          title: mp?.title,
          titleTag: mp?.titleTag,
          itemTitleTag: mp?.itemTitleTag,
          desc: mp?.desc,
          mediaGroup: mp?.mediaGroup?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
              mediaH5Url: item?.mediaH5Url?.url,
            }
          }),
          theme: mp?.theme,
          mediaAspectRatio: mediaAspectRatioMap[mp?.mediaAspectRatio as keyof typeof mediaAspectRatioMap],
          mediaBorderRadius: mp?.mediaBorderRadius,
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F32: // F3-2
      return {
        component: HorizontalMutipleMedia,
        props: {
          type: 'F3-2',
          title: mp?.title,
          desc: mp?.desc,
          mediaGroup: mp?.mediaGroup?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
              mediaH5Url: item?.mediaH5Url?.url,
            }
          }),
          theme: mp?.theme,
          mediaAspectRatio: mediaAspectRatioMap[mp?.mediaAspectRatio as keyof typeof mediaAspectRatioMap],
          mediaBorderRadius: mp?.mediaBorderRadius,
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F4: // F4
      return {
        component: HorizontalMutipleMedia,
        props: {
          type: 'F4',
          title: mp?.title,
          desc: mp?.desc,
          mediaGroup: mp?.mediaGroup?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
              mediaH5Url: item?.mediaH5Url?.url,
            }
          }),
          theme: mp?.theme || Theme.Dark,
          mediaAspectRatio: mediaAspectRatioMap[mp?.mediaAspectRatio as keyof typeof mediaAspectRatioMap],
          mediaBorderRadius: mp?.mediaBorderRadius,
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F31: // F3-1
      return {
        component: MainAndTwoSubMedia,
        props: {
          theme: mp?.theme,
          title: mp?.title,
          desc: mp?.desc,
          mediaGroup: mp?.mediaGroup?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
              mediaH5Url: item?.mediaH5Url?.url,
            }
          }),
          mediaBorderRadius: mp?.mediaBorderRadius,
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F51: // F5-1
      return {
        component: FeaturedMediaGrid,
        props: {
          title: mp?.title,
          desc: mp?.desc,
          mediaGroup: mp?.mediaGroup?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
              mediaH5Url: item?.mediaH5Url?.url,
            }
          }),
          theme: mp?.theme,
          mediaBorderRadius: mp?.mediaBorderRadius,
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F52: // F5-2
      return {
        component: MainAndFourSubMedia,
        props: {
          theme: mp?.theme,
          title: mp?.title,
          desc: mp?.desc,
          mediaGroup: mp?.mediaGroup?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
            }
          }),
          mediaBorderRadius: mp?.mediaBorderRadius,
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F61: // F6-1
      return {
        component: TopTabSwitch,
        props: {
          theme: mp?.theme,
          title: mp?.title,
          desc: mp?.desc,
          mediaBorderRadius: mp?.mediaBorderRadius,
          mediaGroup: mp?.mediaGroup?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
              downloadUrl: item?.downloadUrl?.url,
              jumpUrl: item?.jumpUrl,
            }
          }),
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F62: // F6-2
      return {
        component: TabSwiper,
        props: {
          theme: mp?.theme,
          title: mp?.title,
          desc: mp?.desc,
          mediaUrlList: mp?.mediaUrlList?.map((item: any) => item.url),
          tabPanels: mp?.tabPanels,
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F71: // F7-1
      return {
        component: IconTagArray,
        props: {
          type: 'F7-1',
          theme: mp?.theme,
          title: mp?.title,
          align: mp?.align || 'center',
          dataList: mp?.dataList?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
            }
          }),
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F72: // F7-2
      return {
        component: IconTagArray,
        props: {
          type: 'F7-2',
          theme: mp?.theme,
          title: mp?.title,
          dataList: mp?.dataList?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
            }
          }),
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F73: // F7-3
      return {
        component: IconTagArray,
        props: {
          type: 'F7-3',
          theme: mp?.theme,
          title: mp?.title,
          dataList: mp?.dataList?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
            }
          }),
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.F74: // F7-4
      return {
        component: IconTagArray,
        props: {
          type: 'F7-4',
          theme: mp?.theme,
          title: mp?.title,
          dataList: mp?.dataList?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
            }
          }),
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.C1: // C1
      return {
        component: AccessoriesPanel,
        props: {
          theme: mp?.theme,
          title: mp?.title,
          titleTag: mp?.titleTag,
          desc: mp?.desc,
          productCode: productCode,
          mediaBorderRadius: mp?.mediaBorderRadius,
          mediaH5BorderRadius: mp?.mediaH5BorderRadius,
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.C2: // C2
      return {
        component: MultiCardTiling,
        props: {
          theme: mp?.theme,
          title: mp?.title,
          titleTag: mp?.titleTag,
          itemTitleTag: mp?.itemTitleTag,
          desc: mp?.desc,
          borderRadius: mp?.borderRadius,
          mediaGroup: mp?.mediaGroup?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
            }
          }),
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.C21: // C2-1
      return {
        component: FlipMultipleCards,
        props: {
          theme: mp?.theme,
          dataList: mp?.dataList?.map((item: any) => {
            return {
              ...item,
              items: item?.items?.map((item: any) => {
                return {
                  ...item,
                  mediaUrl: item?.mediaUrl?.url,
                }
              }),
            }
          }),
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px100",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px100",
        },
      }
    case TemplateType.C22: // C2-2
      return {
        component: MultiCardModal,
        props: {
          theme: mp?.theme,
          borderRadius: mp?.borderRadius,
          dataList: mp?.dataList?.map((dataItem: any) => {
            return {
              ...dataItem,
              downloadUrl: dataItem?.downloadUrl?.url,
              cardList: dataItem?.cardList?.map((cardItem: any) => {
                return {
                  ...cardItem,
                  mediaUrl: cardItem?.media?.url,
                  modalMediaGroup: cardItem?.modalMediaGroup?.map((modalItem: any) => {
                    return {
                      ...modalItem,
                      mediaUrl: modalItem?.media?.url,
                    }
                  }),
                }
              }),
            }
          }),
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px100",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px100",
        },
      }

    case TemplateType.C3: // C3
      return {
        component: ProductParamsPanel,
        props: {
          theme: mp?.theme,
          title: mp?.title,
          titleTag: mp?.titleTag,
          imgUrl: mp?.imageUrl?.url,
          paramsList: mp?.paramsList?.map((item: any) => {
            return {
              ...item,
              label: item?.title,
              value: item?.value?.map((item: any) => item.title),
            }
          }),
          btntext: mp?.btnText,
          robot: mp?.robot,
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.C4: // C4
      return {
        component: NotesBar,
        props: {
          theme: mp?.theme,
          dataList: mp?.dataList?.map((item: any) => ({
            title: item.title,
            desc: item.desc,
          })),
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px64",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px64",
        },
      }
    case TemplateType.C5: // C5
      return {
        component: FullScreenTiling,
        props: {
          theme: mp?.theme,
          aspectRatio: mp?.aspectRatio,
          borderRadius: mp?.borderRadius,
          title: mp?.title,
          desc: mp?.desc,
          mediaGroup: mp?.mediaGroup?.map((item: any) => {
            return {
              mediaUrl: item?.url,
            }
          }),
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px0",
        },
      }
    case TemplateType.C6: // C6
      return {
        component: ComparePanel,
        props: {
          title: mp?.title,
          desc: mp?.desc,
          dataList: mp?.dataList?.map((item: any) => {
            return {
              ...item,
              colors: item?.colors?.map((item: any) => {
                return { ...item, imgUrl: item?.imgUrl?.url }
              }),
            }
          }),
          robotCode: mp?.robotCode,
          showColor: mp?.showColor,
          theme: mp?.theme,
          paddingTop: mp?.paddingTop as SectionPaddingValue || "px120",
          paddingBottom: mp?.paddingBottom as SectionPaddingValue || "px120",
        },
      }
    case TemplateType.C7: // C7
      return {
        component: FloatRobot,
        props: {
          robot: mp?.robot?.url,
          color: mp?.color,
          hideCircleBorder: mp?.hideCircleBorder,
          href: mp?.href,
          buttonText: mp?.buttonText,
          buttonTextSize: mp?.buttonTextSize,
          buttonTextColor: mp?.buttonTextColor,
        },
      }
    case TemplateType.S1: // S1
      return {
        component: S1,
        props: {
          theme: mp?.theme,
          title: mp?.title,
          desc: mp?.desc,
          productMedia: mp?.productMedia?.url,
          innerMediaUrlList: mp?.innerMediaUrlList?.map((item: any) => item.url),
        },
      }
    case TemplateType.S2: // S2
      return {
        component: S2,
        props: {
          theme: mp?.theme,
          // title: mp?.title,
          // desc: mp?.desc,
          infoItemList: mp?.infoItemList,
          mediaUrlList: mp?.mediaUrlList?.map((item: any) => item.url),
        },
      }
    case TemplateType.S3: // S3
      return {
        component: ScrollSwitchMedia,
        props: {
          theme: mp?.theme,
          title: mp?.title,
          mediaGroup: mp?.mediaGroup?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
            }
          }),
        },
      }
    case TemplateType.S4: // S4
      return {
        component: S4,
        props: {
          theme: mp?.theme,
          title: mp?.title,
          titleTag: mp?.titleTag,
          itemTitleTag: mp?.itemTitleTag,
          desc: mp?.desc,
          mediaUrl: mp?.mediaUrl?.url,
          tagList: mp?.tagList?.map((item: any) => {
            return {
              ...item,
              mediaUrl: item?.mediaUrl?.url,
            }
          }),
        },
      }
    default:
      throw new Error(`unknown template ${template}, please register it first!`)
  }
}

export function MetaPage({ productId, initialData }: { productId: string; initialData: ProductDetailData }) {
  const locale = useLocale()
  const { data } = useSWR<ProductDescriptionComponent[]>(getProductDetailData.swrkey(locale, productId), () =>
    getProductDetailData(productId, locale)
  )

  const finalData = data ?? initialData
  return (
    <div className="meta-page">
      {finalData?.map((mp: ProductDescriptionComponent, index: number) => {
        const { component: Component, props } = resolveMetaComponent(mp, productId, locale)
        return <Component key={index} {...props} />
      })}
    </div>
  )
}
